// Condition reporting's rules on the device — pure, no storage. The wording,
// scales and roll-ups themselves are @higgins/condition-library's, shared
// with the report; this file maps them to the CRM labels the device stores.
import {
  BAND_DEFAULT_DELIVERY,
  bandOf,
  DELIVERY_MODE_BY_KEY,
  DELIVERY_MODES as LIBRARY_DELIVERY_MODES,
  levelOf,
  SCALES,
  scaleByLabel,
  worseBand,
  type Band,
  type Scale,
} from '@higgins/condition-library';
import { REMOVED_STATE } from './condition-lines.ts';

/** Region Group's scale, the first contract on it (DD, 2026-10-19). A scale's label is its hig_ratingscale label in CRM. */
const DEFAULT_SCALE = SCALES['one-to-three'];
export const DEFAULT_SCALE_LABEL = DEFAULT_SCALE.label;
/** The scales to choose from when a report starts, the default first. */
export const SCALE_LABELS = [DEFAULT_SCALE_LABEL, ...Object.values(SCALES).map((s) => s.label).filter((l) => l !== DEFAULT_SCALE_LABEL)];

/** A visit's scale (its hig_ratingscale label); the default when blank or unknown. */
export const scaleOf = (label: unknown): Scale => scaleByLabel(label) ?? DEFAULT_SCALE;

/** The library's key for a visit's scale. */
export const scaleKeyOf = (label: unknown): string => scaleOf(label).key;

/** hig_band's labels, by band. */
export const BAND_CHOICE: Readonly<Record<Band, 'Poor' | 'Fair' | 'Good'>> = { poor: 'Poor', fair: 'Fair', good: 'Good' };

/** hig_priority: the area's action (DD, 2026-10-21 — audits aren't on maintenance). */
export const ACTIONS = ['Urgent', 'Do soon', 'Do later', 'N/A'] as const;
export type Action = (typeof ACTIONS)[number];

/** The action suggested for an area's band; the measurer can change it. */
export const ACTION_FOR_BAND: Readonly<Record<Band, Action>> = { poor: 'Urgent', fair: 'Do soon', good: 'N/A' };

/** hig_deliverymode's labels, in the library's order (repaint first, monitor last). */
export const DELIVERY_MODES: readonly string[] = LIBRARY_DELIVERY_MODES.map((d) => d.label);

/** The delivery mode suggested for an area's band (the report uses it until one is chosen). */
export const deliveryModeForBand = (band: Band): string => DELIVERY_MODE_BY_KEY[BAND_DEFAULT_DELIVERY[band]].label;

/** How many years the outlook offers, from this one. */
const OUTLOOK_YEARS_OFFERED = 5;

/** The outlook years to choose from: this year and the next four, plus a year already chosen outside them. Pure. */
export function outlookYears(thisYear: number, chosen: number | null): number[] {
  const years = Array.from({ length: OUTLOOK_YEARS_OFFERED }, (_, i) => thisYear + i);
  return chosen !== null && !years.includes(chosen) ? [...years, chosen].sort((a, b) => a - b) : years;
}

/** One rated surface as the area's roll-up needs it: its stored rating value. */
interface Rated {
  rating: string | null;
}

/**
 * The area's rating: its worst rated surface (the library's rule), as the band
 * and that surface's own value on the scale. Null when nothing is rated yet.
 */
export function areaRating(items: readonly Rated[], scaleKey: string): { band: Band; value: string } | null {
  let worst: { band: Band; value: string } | null = null;
  for (const item of items) {
    const band = bandOf(scaleKey, item.rating);
    if (!band || item.rating === null) continue;
    if (!worst || worseBand(worst.band, band) !== worst.band) worst = { band, value: item.rating };
  }
  return worst;
}

/** "2 Fair" on a numbered scale, "Fair" on a named one. */
export function ratingText(scaleKey: string, value: string | null): string {
  const level = levelOf(scaleKey, value);
  if (!level) return '';
  return typeof level.value === 'number' ? `${level.value} ${level.label}` : level.label;
}

// ---------- changing the scale (#473) ----------
// A rating's value only means something on the scale it was made on ("2" is
// Fair on 1–3, Poor on 1–5), so changing a report's scale clears its ratings
// (MJ, 2026-10-23); defects, notes and photos stay.

/**
 * The scale a rating was made on, kept on the device's copy of its rating
 * (never a CRM column): sent beside the save, not in it (forSending), so the
 * server drops a rating made on a scale the report no longer uses — another
 * iPad's, made before it heard of the change.
 */
export const RATED_ON = 'ratedOnScale';

/** A rating taken off: its value and band (a scale change, a restored line's rating made on another scale). */
export const CLEARED = { hig_rating: null, hig_band: null };

/** One condition rating record as these rules need it (an observation). */
interface RatingRecord {
  table: string;
  parentId: string;
  fields: Readonly<Record<string, unknown>>;
}

const hasRating = (r: RatingRecord): boolean => r.table === 'observation' && r.fields.hig_rating !== null && r.fields.hig_rating !== undefined && r.fields.hig_rating !== '';

/**
 * The job's rated surfaces still on the report: what a scale change counts and
 * clears. A line taken off the report (#509) keeps its rating and band as they
 * were, for the history.
 */
export const ratedObservations = <T extends RatingRecord>(records: readonly T[]): T[] =>
  records.filter((r) => hasRating(r) && r.fields.statecode !== REMOVED_STATE);

/** What a scale change would clear: how many ratings, on how many areas. */
export function ratingsToClear(records: readonly RatingRecord[]): { ratings: number; areas: number } {
  const rated = ratedObservations(records);
  return { ratings: rated.length, areas: new Set(rated.map((r) => r.parentId)).size };
}

const counted = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "This clears 7 ratings on 3 areas." */
export const clearingText = ({ ratings, areas }: { ratings: number; areas: number }): string =>
  `This clears ${counted(ratings, 'rating')} on ${counted(areas, 'area')}.`;

/** The fields a rating save adds: the scale it was made on (the visit's, as the device has it now). */
export const ratedOn = (scaleLabel: unknown): Record<string, string> => ({ [RATED_ON]: scaleOf(scaleLabel).label });

/**
 * Ratings made on another scale than the visit's: this device's own, made
 * before it heard the scale had changed. They are cleared here as the change
 * cleared everyone else's. A rating with no stamp is CRM's own copy, or from
 * before the stamp: left as it is.
 */
export function staleRatings<T extends RatingRecord>(records: readonly T[], visitScaleLabel: unknown): T[] {
  const scale = scaleOf(visitScaleLabel).label;
  return ratedObservations(records).filter((r) => typeof r.fields[RATED_ON] === 'string' && scaleOf(r.fields[RATED_ON]).label !== scale);
}

/**
 * Whether the visit's queued save has to move behind the rest before its new
 * scale is put on it: CRM clears every rating when the scale changes, so a
 * rating's save still queued behind it would arrive at an etag CRM has moved
 * on from, and be set aside as a conflict.
 */
export function visitMustGoLast(entries: readonly { seq?: number; targetId: string; kind: string }[], visitId: string): boolean {
  const visitSeq = entries.find((e) => e.targetId === visitId && e.kind === 'record')?.seq;
  return visitSeq !== undefined && entries.some((e) => e.targetId !== visitId && (e.seq ?? 0) > visitSeq);
}

/**
 * The scale a visit was changed from on this device, kept on its copy (never a
 * CRM column) until CRM has the change: sent beside the save, so a save CRM
 * took but couldn't finish clearing the ratings for finishes it when sent again.
 */
export const SCALE_FROM = 'scaleChangedFrom';

/** The visit's fields for a scale change: the new scale, and the one first changed from. */
export const scaleChangeFields = (stored: Readonly<Record<string, unknown>>, scaleLabel: string): Record<string, string> => ({
  hig_ratingscale: scaleLabel,
  [SCALE_FROM]: typeof stored[SCALE_FROM] === 'string' ? stored[SCALE_FROM] : scaleOf(stored.hig_ratingscale).label,
});

/**
 * A record's fields as CRM takes them (the device's own fields taken out), the
 * scale its rating was made on, and the scale a visit was changed from. A rating
 * with no stamp is CRM's own copy, downloaded with the visit (`visitScale`, the
 * device's copy of the visit's): it goes as made on that, so an old copy re-sent
 * with a note edit can't come back on a new scale.
 */
export function forSending<F extends Readonly<Record<string, unknown>>>(
  record: { table: string; parentId: string; fields: F },
  visitScale?: unknown,
): { fields: F; ratingScale?: string; scaleFrom?: string } {
  const { [RATED_ON]: stamp, [SCALE_FROM]: scaleFrom, ...rest } = record.fields;
  const fields = RATED_ON in record.fields || SCALE_FROM in record.fields ? (rest as F) : record.fields;
  if (typeof scaleFrom === 'string') return { fields, scaleFrom };
  if (!hasRating(record)) return { fields };
  const ratingScale = typeof stamp === 'string' ? stamp : visitScale !== undefined ? scaleOf(visitScale).label : undefined;
  return { fields, ...(ratingScale ? { ratingScale } : {}) };
}

// ---------- the JSON columns ----------

const JSON_VERSION = 1;

function parseList(raw: unknown, field: string): string[] {
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const list = (JSON.parse(raw) as Record<string, unknown>)[field];
    return Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** hig_visitarea.hig_conditiontags: the condition ticks (hig_accesstag keys), read for a visit area with no tick rows (tick-rows.ts). */
export const parseTicks = (raw: unknown): string[] => parseList(raw, 'tags');

/** hig_conditionobservation.hig_defects: the library's defect ids, plus any typed by the measurer. */
export interface DefectsDoc {
  ids: string[];
  custom: string[];
}
export const parseDefects = (raw: unknown): DefectsDoc => ({ ids: parseList(raw, 'ids'), custom: parseList(raw, 'custom') });
export const serialiseDefects = (doc: DefectsDoc): string => JSON.stringify({ schemaVersion: JSON_VERSION, ids: doc.ids, custom: doc.custom });

export function toggle(list: readonly string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
}

// ---------- area colours on the site plan ----------

/** Each area's colour, by its place in the list: the library's, so the report's cards match the plan's pins. */
export { areaColour } from '@higgins/condition-library';

/** The site-requirement category condition ticks are kept under (hig_accesstag.hig_category). */
export const CONDITION_CATEGORY = 'Condition';
